import { useAppSelector } from 'app/hooks';
import React, { Suspense } from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';

// Lazy load NotFound for access denied
const NotFound = React.lazy(() => import('../pages/NotFound'));

type PrivateRouteProps = {
    role?: string; // single role to allow
    excludeRoles?: string[]; // roles to block
};

const loading = () => <div className=""></div>;

const PrivateRoute = ({ role, excludeRoles }: PrivateRouteProps) => {
    const location = useLocation();

    // get auth state from redux
    const { user, token } = useAppSelector((state) => state.auth);

    // not logged in → redirect to login
    if (!token || !user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // if excludeRoles is provided, block those roles
    if (excludeRoles && excludeRoles.includes(user.role)) {
        // Redirect superadmin to their dashboard
        if (user.role === 'superadmin') {
            return <Navigate to="/super-admin/dashboard" replace />;
        }
        // Show 404 for blocked roles
        return (
            <Suspense fallback={loading()}>
                <NotFound />
            </Suspense>
        );
    }

    // if role is provided, check user's role
    if (role && user.role !== role) {
        // role mismatch → show 404 page
        return (
            <Suspense fallback={loading()}>
                <NotFound />
            </Suspense>
        );
    }

    return (
        <Suspense fallback={loading()}>
            <Outlet />
        </Suspense>
    );
};

export default PrivateRoute;
